import React from 'react';
import type { FeedbackItem, FeedbackItemType, LikertConfig } from '../../../../../types/course';
import FloatingLabelPill from '../shared/FloatingLabelPill';
import SectionHeader from '../shared/SectionHeader';
import SegmentedChips from '../shared/SegmentedChips';
import LikertScalePreview from '../shared/LikertScalePreview';
import TrashButton from '../shared/TrashButton';

type LikertType = Extract<FeedbackItemType, 'likert_3_scale' | 'likert_5_scale' | 'likert_10_scale'>;

type LikertItem = Extract<FeedbackItem, { type: LikertType }>;

interface ItemEditorProps {
  item: LikertItem;
  onChange: (patch: Partial<LikertItem>) => void;
  onDelete: () => void;
  readOnly?: boolean;
}

const SCALE_OPTIONS: ReadonlyArray<{ value: LikertType; label: string }> = [
  { value: 'likert_3_scale', label: '3' },
  { value: 'likert_5_scale', label: '5' },
  { value: 'likert_10_scale', label: '10' },
];

function scaleFor(type: LikertType): 3 | 5 | 10 {
  if (type === 'likert_3_scale') return 3;
  if (type === 'likert_5_scale') return 5;
  return 10;
}

export default function LikertScaleItem({ item, onChange, onDelete, readOnly }: ItemEditorProps) {
  const { question } = item.config;

  const updateConfig = (patch: Partial<LikertConfig>) => {
    onChange({ config: { ...item.config, ...patch } } as Partial<LikertItem>);
  };

  const handleScaleChange = (type: LikertType) => {
    onChange({ type } as Partial<LikertItem>);
  };

  return (
    <div className="flex flex-col gap-[31px] w-full">
      <SectionHeader title="Scaling answer" />
      <FloatingLabelPill
        label="Question"
        value={question}
        onChange={(v) => updateConfig({ question: v })}
        placeholder="How would you rate…?"
        size="xl"
        readOnly={readOnly}
        trailingIcon={!readOnly && <TrashButton onClick={onDelete} ariaLabel="Delete question" />}
      />
      <div className="flex flex-col md:flex-row gap-4 md:items-end md:justify-between">
        <SegmentedChips
          label="Scale size"
          options={SCALE_OPTIONS}
          value={item.type}
          onChange={handleScaleChange}
          disabled={readOnly}
        />
        <label
          className={`flex items-center gap-2 font-sans text-[13px] text-text-secondary ${
            readOnly ? 'cursor-default' : 'cursor-pointer'
          }`}
        >
          <input
            type="checkbox"
            checked={!!item.required}
            onChange={(e) => onChange({ required: e.target.checked } as Partial<LikertItem>)}
            disabled={readOnly}
            className="accent-primary-500"
          />
          Required
        </label>
      </div>
      <div className="p-4 rounded-2xl bg-primary-500/10 border border-dashed border-primary-500/20">
        <LikertScalePreview
          scale={scaleFor(item.type)}
          leftLabel="Strongly disagree"
          rightLabel="Strongly agree"
          interactive
        />
      </div>
    </div>
  );
}
